import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TTicketActivity } from '../../ticket/types/ticket.type';

export type TGetTicketActivityResponse = ApiResponse<{
  spaceLogs: TTicketActivity[];
  pageInfo: {
    hasNextPage: boolean;
    nextCursor: string | null;
  };
}>;
export type TGetTicketActivityParams = {
  ticketId: string;
  limit?: number;
  cursor?: string;
};

export const getTicketActivity = async (params: TGetTicketActivityParams): Promise<TGetTicketActivityResponse> => {
  const url = '/ticket/activity';

  return axiosInstance.get(url, { params });
};

export const GET_TICKET_ACTIVITY_QUERY_KEY = 'get-ticket-activity';

export const getTicketActivityQueryOptions = (params: TGetTicketActivityParams) => {
  return queryOptions({
    queryKey: [
      GET_TICKET_ACTIVITY_QUERY_KEY,
      {
        ticketId: params?.ticketId,
        limit: params?.limit,
        cursor: params?.cursor,
      },
    ],
    queryFn: () => getTicketActivity(params),
  });
};

type UseTicketActivityQueryOptions = {
  params: TGetTicketActivityParams;
  queryConfig?: QueryConfig<typeof getTicketActivityQueryOptions>;
};

export const useTicketActivityQuery = ({ queryConfig, params }: UseTicketActivityQueryOptions) => {
  return useQuery({ ...getTicketActivityQueryOptions(params), ...queryConfig });
};

type UseTicketActivityInfiniteQueryOptions = {
  params: Omit<TGetTicketActivityParams, 'cursor'>;
  queryConfig?: Omit<
    Parameters<typeof useInfiniteQuery<TGetTicketActivityResponse>>[0],
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >;
};

export const useTicketActivityInfiniteQuery = ({ queryConfig, params }: UseTicketActivityInfiniteQueryOptions) => {
  return useInfiniteQuery<TGetTicketActivityResponse>({
    queryKey: [
      GET_TICKET_ACTIVITY_QUERY_KEY,
      {
        ticketId: params?.ticketId,
        limit: params?.limit,
      },
    ],
    queryFn: ({ pageParam }): Promise<TGetTicketActivityResponse> =>
      getTicketActivity({
        ...params,
        cursor: pageParam ? String(pageParam) : undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      return lastPage?.payload?.pageInfo?.nextCursor ?? undefined;
    },
    ...queryConfig,
  });
};
